'use client';

import React, { useState, useMemo } from 'react';
import { useBinanceStream } from './useBinanceStream';

const TICK_SIZES = [0.0001, 0.001, 0.01, 0.1, 1, 10, 100, 1000];

// Format tiền tệ USD (B, M, k)
const formatUSD = (val) => {
  if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
  if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
  return `$${val.toFixed(2)}`;
};

// Helper: build symbol cho từng market
const buildSymbolForMarket = (input, market) => {
  const raw = String(input || '').trim();
  if (!raw) return raw;
  if (market === 'alpha') {
    const lc = raw.toLowerCase();
    return lc.startsWith('alpha_') ? lc : `alpha_${lc}`;
  }
  return raw.toUpperCase();
};

export default function OrderBookAnalyzer({ defaultSymbol = 'BTCUSDT' }) {
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [inputSymbol, setInputSymbol] = useState(defaultSymbol);

  // Symbol riêng cho Alpha (mặc định alpha_116usdt)
  const [alphaSymbol, setAlphaSymbol] = useState('alpha_116usdt');

  const [isRunning, setIsRunning] = useState(false);
  const [secondaryType, setSecondaryType] = useState('spot'); // 'spot' | 'alpha'
  const [selectedTickSize, setSelectedTickSize] = useState(1);

  // ⚡ Luồng 1: Futures (cố định)
  const futuresStream = useBinanceStream(symbol, isRunning, 'futures', 'futures_inst');

  // ⚡ Luồng 2: Spot hoặc Alpha
  const secondaryInput = secondaryType === 'alpha' ? alphaSymbol : symbol;
  const secondaryStream = useBinanceStream(
    secondaryInput,
    isRunning,
    secondaryType,
    'secondary_inst'
  );

  const handleStartStop = () => {
    setIsRunning(!isRunning);
  };

  const handleReset = () => {
    futuresStream.streamData.bidsDiff = {};
    futuresStream.streamData.asksDiff = {};
    secondaryStream.streamData.bidsDiff = {};
    secondaryStream.streamData.asksDiff = {};
  };

  // Xử lý gom nhóm dữ liệu và trích xuất Top 10 Whale Walls
  const processBookData = (rawBids, rawAsks, tick) => {
    const aggregatedBids = {};
    Object.entries(rawBids || {}).forEach(([pStr, vol]) => {
      const p = parseFloat(pStr);
      const roundedPrice = Math.floor(p / tick) * tick;
      const key = roundedPrice.toFixed(
        tick < 1 ? Math.abs(Math.floor(Math.log10(tick))) : 2
      );
      aggregatedBids[key] = (aggregatedBids[key] || 0) + vol;
    });

    const aggregatedAsks = {};
    Object.entries(rawAsks || {}).forEach(([pStr, vol]) => {
      const p = parseFloat(pStr);
      const roundedPrice = Math.ceil(p / tick) * tick;
      const key = roundedPrice.toFixed(
        tick < 1 ? Math.abs(Math.floor(Math.log10(tick))) : 2
      );
      aggregatedAsks[key] = (aggregatedAsks[key] || 0) + vol;
    });

    const bidsArray = Object.entries(aggregatedBids)
      .map(([p, v]) => ({ price: parseFloat(p), volume: v }))
      .sort((a, b) => b.price - a.price);

    const asksArray = Object.entries(aggregatedAsks)
      .map(([p, v]) => ({ price: parseFloat(p), volume: v }))
      .sort((a, b) => a.price - b.price);

    const maxBidVol = Math.max(...bidsArray.map((item) => item.volume), 1);
    const maxAskVol = Math.max(...asksArray.map((item) => item.volume), 1);

    const top10WhaleBids = bidsArray
      .slice()
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 10);
    const top10WhaleAsks = asksArray
      .slice()
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 10);

    return {
      bids: bidsArray.slice(0, 20),
      asks: asksArray.slice(0, 20).reverse(),
      maxBidVol,
      maxAskVol,
      top10WhaleBids,
      top10WhaleAsks
    };
  };

  const futuresBook = useMemo(() => {
    return processBookData(
      futuresStream.streamData.bidsDiff,
      futuresStream.streamData.asksDiff,
      selectedTickSize
    );
  }, [
    futuresStream.streamData.bidsDiff,
    futuresStream.streamData.asksDiff,
    selectedTickSize,
    futuresStream.diffPulse
  ]);

  const secondaryBook = useMemo(() => {
    return processBookData(
      secondaryStream.streamData.bidsDiff,
      secondaryStream.streamData.asksDiff,
      selectedTickSize
    );
  }, [
    secondaryStream.streamData.bidsDiff,
    secondaryStream.streamData.asksDiff,
    selectedTickSize,
    secondaryStream.diffPulse
  ]);

  const priceDecimals =
    selectedTickSize < 1 ? Math.abs(Math.floor(Math.log10(selectedTickSize))) : 2;

  return (
    <div
      style={{
        backgroundColor: '#0b0e14',
        color: '#fff',
        padding: '20px',
        borderRadius: '12px',
        fontFamily: 'sans-serif',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}
    >
      {/* ================= HÀNG 1: HEADER & CONTROL PANEL ================= */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#111827',
          padding: '12px 16px',
          borderRadius: '8px',
          flexWrap: 'wrap',
          gap: '10px'
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#60a5fa' }}>
            📊 DUAL ORDERBOOK & FULL WHALE WALLS ANALYZER
          </h2>
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>
            So sánh Futures và {secondaryType.toUpperCase()} trực quan.
          </span>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={secondaryType}
            onChange={(e) => setSecondaryType(e.target.value)}
            disabled={isRunning}
            style={{
              backgroundColor: '#1e293b',
              color: '#fff',
              border: '1px solid #334155',
              padding: '6px 10px',
              borderRadius: '6px',
              fontWeight: 'bold'
            }}
          >
            <option value="spot">Secondary: Spot</option>
            <option value="alpha">Secondary: Alpha Stream</option>
          </select>

          <input
            type="text"
            value={inputSymbol}
            onChange={(e) => setInputSymbol(e.target.value)}
            placeholder="BTCUSDT"
            disabled={isRunning}
            style={{
              backgroundColor: '#1e293b',
              color: '#fff',
              border: '1px solid #334155',
              padding: '6px 10px',
              borderRadius: '6px',
              width: '90px',
              fontWeight: 'bold',
              textTransform: 'uppercase'
            }}
          />

          <button
            onClick={() => setSymbol(buildSymbolForMarket(inputSymbol, 'futures'))}
            disabled={isRunning}
            style={{
              backgroundColor: '#334155',
              color: '#fff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Đổi Coin
          </button>

          <button
            onClick={handleStartStop}
            style={{
              backgroundColor: isRunning ? '#dc2626' : '#16a34a',
              color: '#fff',
              border: 'none',
              padding: '6px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {isRunning ? '⏹ STOP' : '▶ START'}
          </button>

          <button
            onClick={handleReset}
            style={{
              backgroundColor: '#475569',
              color: '#fff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            🔄 Reset
          </button>
        </div>
      </div>

      {/* ================= HÀNG 1.5: ALPHA SYMBOL INPUT ================= */}
      {secondaryType === 'alpha' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: '#1a1033',
            padding: '10px 16px',
            borderRadius: '8px',
            border: '1px solid #a855f7'
          }}
        >
          <span style={{ fontSize: '12px', color: '#c084fc', fontWeight: 'bold' }}>
            🅰️ ALPHA SYMBOL:
          </span>
          <input
            type="text"
            value={alphaSymbol}
            onChange={(e) => setAlphaSymbol(e.target.value)}
            placeholder="alpha_116usdt"
            disabled={isRunning}
            style={{
              backgroundColor: '#1e293b',
              color: '#fff',
              border: '1px solid #a855f7',
              padding: '6px 10px',
              borderRadius: '6px',
              width: '200px',
              fontWeight: 'bold',
              fontSize: '12px',
              fontFamily: 'monospace'
            }}
          />
          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
            Ví dụ: <code style={{ color: '#c084fc' }}>alpha_116usdt</code>,{' '}
            <code style={{ color: '#c084fc' }}>alpha_btcusdt</code>
          </span>
        </div>
      )}

      {/* ================= HÀNG 2: TICK SIZE SELECTOR ================= */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: '#111827',
          padding: '10px 16px',
          borderRadius: '8px'
        }}
      >
        <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold' }}>
          TICK SIZE:
        </span>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {TICK_SIZES.map((tick) => (
            <button
              key={tick}
              onClick={() => setSelectedTickSize(tick)}
              style={{
                backgroundColor: selectedTickSize === tick ? '#2563eb' : '#1f2937',
                color: '#fff',
                border: 'none',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: selectedTickSize === tick ? 'bold' : 'normal'
              }}
            >
              {tick}
            </button>
          ))}
        </div>
      </div>

      {/* ================= HÀNG 3: SONG SONG 2 ORDERBOOK ================= */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* ORDERBOOK: FUTURES */}
        <div
          style={{
            background: '#111827',
            padding: '12px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
              borderBottom: '1px solid #1f2937',
              paddingBottom: '4px'
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#38bdf8' }}>
              📈 FUTURES ORDERBOOK ({symbol})
            </span>
            <span style={{ fontSize: '11px', color: futuresStream.wsStatus.color }}>
              {futuresStream.wsStatus.text}
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              fontSize: '10px',
              color: '#94a3b8',
              fontWeight: 'bold'
            }}
          >
            <span>GIÁ</span>
            <span style={{ textAlign: 'right' }}>VOL</span>
            <span style={{ textAlign: 'right' }}>VAL</span>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1px',
              maxHeight: '180px',
              overflowY: 'auto'
            }}
          >
            {futuresBook.asks.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  fontSize: '11px',
                  position: 'relative',
                  padding: '1px 0'
                }}
              >
                <span style={{ color: '#ef4444', fontWeight: 'bold', fontFamily: 'monospace' }}>
                  {item.price.toFixed(priceDecimals)}
                </span>
                <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                  {item.volume.toFixed(2)}
                </span>
                <span
                  style={{
                    textAlign: 'right',
                    color: '#94a3b8',
                    fontFamily: 'monospace'
                  }}
                >
                  {formatUSD(item.price * item.volume)}
                </span>
              </div>
            ))}
          </div>

          <div
            style={{
              padding: '6px 0',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: '900',
              color: '#38bdf8',
              borderTop: '1px solid #1f2937',
              borderBottom: '1px solid #1f2937'
            }}
          >
            ⚡ $
            {futuresStream.streamData.price
              ? futuresStream.streamData.price.toLocaleString()
              : '---'}
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1px',
              maxHeight: '180px',
              overflowY: 'auto'
            }}
          >
            {futuresBook.bids.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  fontSize: '11px',
                  position: 'relative',
                  padding: '1px 0'
                }}
              >
                <span style={{ color: '#22c55e', fontWeight: 'bold', fontFamily: 'monospace' }}>
                  {item.price.toFixed(priceDecimals)}
                </span>
                <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                  {item.volume.toFixed(2)}
                </span>
                <span
                  style={{
                    textAlign: 'right',
                    color: '#94a3b8',
                    fontFamily: 'monospace'
                  }}
                >
                  {formatUSD(item.price * item.volume)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ORDERBOOK: SPOT / ALPHA */}
        <div
          style={{
            background: '#111827',
            padding: '12px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
              borderBottom: '1px solid #1f2937',
              paddingBottom: '4px'
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#a855f7' }}>
              📈 {secondaryType.toUpperCase()} ORDERBOOK ({secondaryInput})
            </span>
            <span style={{ fontSize: '11px', color: secondaryStream.wsStatus.color }}>
              {secondaryStream.wsStatus.text}
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              fontSize: '10px',
              color: '#94a3b8',
              fontWeight: 'bold'
            }}
          >
            <span>GIÁ</span>
            <span style={{ textAlign: 'right' }}>VOL</span>
            <span style={{ textAlign: 'right' }}>VAL</span>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1px',
              maxHeight: '180px',
              overflowY: 'auto'
            }}
          >
            {secondaryBook.asks.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  fontSize: '11px',
                  position: 'relative',
                  padding: '1px 0'
                }}
              >
                <span style={{ color: '#ef4444', fontWeight: 'bold', fontFamily: 'monospace' }}>
                  {item.price.toFixed(priceDecimals)}
                </span>
                <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                  {item.volume.toFixed(2)}
                </span>
                <span
                  style={{
                    textAlign: 'right',
                    color: '#94a3b8',
                    fontFamily: 'monospace'
                  }}
                >
                  {formatUSD(item.price * item.volume)}
                </span>
              </div>
            ))}
          </div>

          <div
            style={{
              padding: '6px 0',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: '900',
              color: '#a855f7',
              borderTop: '1px solid #1f2937',
              borderBottom: '1px solid #1f2937'
            }}
          >
            ⚡ $
            {secondaryStream.streamData.price
              ? secondaryStream.streamData.price.toLocaleString()
              : '---'}
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1px',
              maxHeight: '180px',
              overflowY: 'auto'
            }}
          >
            {secondaryBook.bids.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  fontSize: '11px',
                  position: 'relative',
                  padding: '1px 0'
                }}
              >
                <span style={{ color: '#22c55e', fontWeight: 'bold', fontFamily: 'monospace' }}>
                  {item.price.toFixed(priceDecimals)}
                </span>
                <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                  {item.volume.toFixed(2)}
                </span>
                <span
                  style={{
                    textAlign: 'right',
                    color: '#94a3b8',
                    fontFamily: 'monospace'
                  }}
                >
                  {formatUSD(item.price * item.volume)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================= HÀNG 4: TOP 10 WHALE WALLS ================= */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* CỘT TRÁI: FUTURES WHALE WALLS */}
        <div
          style={{
            background: '#111827',
            padding: '12px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          <span
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#38bdf8',
              borderBottom: '1px solid #1f2937',
              paddingBottom: '4px'
            }}
          >
            🐋 FUTURES - TOP 10 WHALE WALLS (TỔNG QUAN)
          </span>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: 'bold' }}>
                🟢 TOP 10 BIDS
              </span>
              {futuresBook.top10WhaleBids.map((wall, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(34, 197, 94, 0.05)',
                    borderLeft: '2px solid #22c55e',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    fontSize: '10px'
                  }}
                >
                  <span style={{ fontWeight: 'bold', color: '#22c55e', fontFamily: 'monospace' }}>
                    @{wall.price.toFixed(priceDecimals)}
                  </span>
                  <div style={{ textAlign: 'right' }}>
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#fff',
                        fontFamily: 'monospace',
                        marginRight: '6px'
                      }}
                    >
                      {wall.volume.toFixed(1)}
                    </span>
                    <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                      {formatUSD(wall.price * wall.volume)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span style={{ fontSize: '10px', color: '#ef4444', fontWeight: 'bold' }}>
                🔴 TOP 10 ASKS
              </span>
              {futuresBook.top10WhaleAsks.map((wall, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(239, 68, 68, 0.05)',
                    borderLeft: '2px solid #ef4444',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    fontSize: '10px'
                  }}
                >
                  <span style={{ fontWeight: 'bold', color: '#ef4444', fontFamily: 'monospace' }}>
                    @{wall.price.toFixed(priceDecimals)}
                  </span>
                  <div style={{ textAlign: 'right' }}>
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#fff',
                        fontFamily: 'monospace',
                        marginRight: '6px'
                      }}
                    >
                      {wall.volume.toFixed(1)}
                    </span>
                    <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                      {formatUSD(wall.price * wall.volume)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CỘT PHẢI: SPOT / ALPHA WHALE WALLS */}
        <div
          style={{
            background: '#111827',
            padding: '12px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          <span
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#a855f7',
              borderBottom: '1px solid #1f2937',
              paddingBottom: '4px'
            }}
          >
            🐋 {secondaryType.toUpperCase()} - TOP 10 WHALE WALLS (TỔNG QUAN)
          </span>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: 'bold' }}>
                🟢 TOP 10 BIDS
              </span>
              {secondaryBook.top10WhaleBids.map((wall, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(34, 197, 94, 0.05)',
                    borderLeft: '2px solid #22c55e',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    fontSize: '10px'
                  }}
                >
                  <span style={{ fontWeight: 'bold', color: '#22c55e', fontFamily: 'monospace' }}>
                    @{wall.price.toFixed(priceDecimals)}
                  </span>
                  <div style={{ textAlign: 'right' }}>
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#fff',
                        fontFamily: 'monospace',
                        marginRight: '6px'
                      }}
                    >
                      {wall.volume.toFixed(1)}
                    </span>
                    <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                      {formatUSD(wall.price * wall.volume)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span style={{ fontSize: '10px', color: '#ef4444', fontWeight: 'bold' }}>
                🔴 TOP 10 ASKS
              </span>
              {secondaryBook.top10WhaleAsks.map((wall, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(239, 68, 68, 0.05)',
                    borderLeft: '2px solid #ef4444',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    fontSize: '10px'
                  }}
                >
                  <span style={{ fontWeight: 'bold', color: '#ef4444', fontFamily: 'monospace' }}>
                    @{wall.price.toFixed(priceDecimals)}
                  </span>
                  <div style={{ textAlign: 'right' }}>
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#fff',
                        fontFamily: 'monospace',
                        marginRight: '6px'
                      }}
                    >
                      {wall.volume.toFixed(1)}
                    </span>
                    <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                      {formatUSD(wall.price * wall.volume)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
